import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { AdminMember, EditMemberRequest } from '@grc/shared';
import { ApiError } from '../../api';
import { ErrorLine, NoteLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { useMember, useSession } from '../../session';
import {
  disableMember,
  editMember,
  enableMember,
  listMembers,
  removeMember,
  resetPassword,
} from './admin-api';
import { checkValues, MemberFields, valuesOf, type MemberValues } from './MemberFields';
import type { RemovedState } from './Members';
import { MemberTags } from './MemberTags';
import { OneTimePassword } from './OneTimePassword';

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'gone' }
  | { status: 'loaded'; member: AdminMember };

/**
 * A member's page (A5; D23, D25), at /admin/members/:id: their details to edit, and what an admin
 * can do to the account: disable or enable it, reset the password, remove the member, the last two
 * after a question. The admin's own page offers the name and the username only (the owner's rule,
 * D23): another admin does the rest, and Profile has the change of their password (A6; D26).
 */
export function Member() {
  const { id = '' } = useParams();
  // Each member's page starts afresh, from loading.
  return <MemberPage key={id} id={id} />;
}

function MemberPage({ id }: { id: string }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    // The API has no route for one member: the list is a club's, a few dozen at most.
    listMembers().then(
      (members) => {
        if (!current) return;
        const member = members.find((m) => m.id === id);
        setLoad(member === undefined ? { status: 'gone' } : { status: 'loaded', member });
      },
      (error: unknown) => {
        if (current) setLoad({ status: 'failed', error });
      },
    );
    return () => {
      current = false;
    };
  }, [id, attempt]);

  function retry() {
    setLoad({ status: 'loading' });
    setAttempt((n) => n + 1);
  }

  return (
    <section className="screen screen-narrow">
      <Link to="/admin/members" className="back-link">
        {t('member.back')}
      </Link>
      {load.status === 'loading' && <p className="muted">{t('app.loading')}</p>}
      {load.status === 'failed' && (
        <div className="stack">
          <ErrorLine text={errorText(load.error)} />
          <button type="button" className="button" onClick={retry}>
            {t('app.retry')}
          </button>
        </div>
      )}
      {load.status === 'gone' && <p>{t('member.gone')}</p>}
      {load.status === 'loaded' && (
        <MemberDetails member={load.member} onGone={() => setLoad({ status: 'gone' })} />
      )}
    </section>
  );
}

type Action = 'save' | 'disable' | 'enable' | 'reset' | 'remove';

function MemberDetails({ member: first, onGone }: { member: AdminMember; onGone: () => void }) {
  const me = useMember();
  const { memberEdited } = useSession();
  const navigate = useNavigate();
  const own = first.id === me.id;
  const [member, setMember] = useState(first);
  const [values, setValues] = useState<MemberValues>(() => valuesOf(first));
  const [busy, setBusy] = useState<Action>();
  const [confirming, setConfirming] = useState<'reset' | 'remove'>();
  const [formError, setFormError] = useState<string>();
  const [formNote, setFormNote] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [actionNote, setActionNote] = useState<string>();
  // The reset's one-time password, while it's shown (OneTimePassword).
  const [password, setPassword] = useState<string>();
  const displayNameRef = useRef<HTMLInputElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  const idle = busy === undefined;
  const changed =
    values.displayName.trim() !== member.displayName ||
    values.username.trim().toLowerCase() !== member.username ||
    (!own && values.role !== member.role);

  /**
   * One change at a time: the lines cleared, its button busy, and what went wrong said where it
   * was asked for. A member removed meanwhile (by another admin) is gone.
   */
  async function run(action: Action, where: 'form' | 'actions', change: () => Promise<void>) {
    setBusy(action);
    setFormError(undefined);
    setFormNote(undefined);
    setActionError(undefined);
    setActionNote(undefined);
    try {
      await change();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'not_found') {
        onGone();
        return;
      }
      (where === 'form' ? setFormError : setActionError)(errorText(err));
    }
    setBusy(undefined);
  }

  async function onSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!idle) return;
    const checked = checkValues(values);
    if (checked.kind === 'empty') {
      (checked.field === 'displayName' ? displayNameRef : usernameRef).current?.focus();
      return;
    }
    if (checked.kind === 'rule') {
      setFormNote(undefined);
      setFormError(t(checked.error));
      return;
    }
    // Only what differs is sent: the button stays off until something does.
    const edit: EditMemberRequest = {};
    if (checked.displayName !== member.displayName) edit.displayName = checked.displayName;
    if (checked.username !== member.username) edit.username = checked.username;
    if (!own && values.role !== member.role) edit.role = values.role;
    if (Object.keys(edit).length === 0) return;
    await run('save', 'form', async () => {
      const saved = await editMember(member.id, edit);
      setMember(saved);
      setValues(valuesOf(saved));
      // The admin's own new name reaches Home's welcome.
      memberEdited(saved);
      setFormNote(t('member.saved'));
    });
  }

  function onDisable() {
    setConfirming(undefined);
    void run('disable', 'actions', async () => {
      setMember(await disableMember(member.id));
      setActionNote(t('member.disabledNote'));
    });
  }

  function onEnable() {
    setConfirming(undefined);
    void run('enable', 'actions', async () => {
      setMember(await enableMember(member.id));
      setActionNote(t('member.enabledNote'));
    });
  }

  function onReset() {
    void run('reset', 'actions', async () => {
      const answer = await resetPassword(member.id);
      setMember(answer.member);
      setConfirming(undefined);
      setPassword(answer.password);
    });
  }

  function onRemove() {
    void run('remove', 'actions', async () => {
      await removeMember(member.id);
      const state: RemovedState = { removed: member.displayName };
      navigate('/admin/members', { state });
    });
  }

  if (password !== undefined) {
    return (
      <OneTimePassword
        title="oneTime.resetTitle"
        member={member}
        password={password}
        onDone={() => setPassword(undefined)}
      />
    );
  }

  return (
    <>
      <h1>{member.displayName}</h1>
      <p className="muted member-username">{member.username}</p>
      <MemberTags member={member} own={own} />
      {own && (
        <>
          <p className="note-line member-own">{t('member.own')}</p>
          <Link to="/profile/password" className="text-link">
            {t('member.ownPassword')}
          </Link>
        </>
      )}
      <form className="form" onSubmit={onSave} noValidate>
        <MemberFields
          values={values}
          onChange={setValues}
          withRole={!own}
          displayNameRef={displayNameRef}
          usernameRef={usernameRef}
        />
        <ErrorLine text={formError} />
        <NoteLine text={formNote} />
        <button type="submit" className="button" disabled={!changed || !idle}>
          {busy === 'save' ? t('member.saving') : t('member.save')}
        </button>
      </form>
      {!own && (
        <div className="actions">
          <ErrorLine text={actionError} />
          <NoteLine text={actionNote} />
          {member.status === 'active' ? (
            <button
              type="button"
              className="button button-secondary"
              onClick={onDisable}
              disabled={!idle}
            >
              {busy === 'disable' ? t('member.disabling') : t('member.disable')}
            </button>
          ) : (
            <button
              type="button"
              className="button button-secondary"
              onClick={onEnable}
              disabled={!idle}
            >
              {busy === 'enable' ? t('member.enabling') : t('member.enable')}
            </button>
          )}
          {confirming === 'reset' ? (
            <Confirm
              question={t('member.resetConfirm', { name: member.displayName })}
              yes={busy === 'reset' ? t('member.resetting') : t('member.resetYes')}
              idle={idle}
              onYes={onReset}
              onCancel={() => setConfirming(undefined)}
            />
          ) : (
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setConfirming('reset')}
              disabled={!idle}
            >
              {t('member.reset')}
            </button>
          )}
          {confirming === 'remove' ? (
            <Confirm
              question={t('member.removeConfirm', { name: member.displayName })}
              yes={busy === 'remove' ? t('member.removing') : t('member.removeYes')}
              idle={idle}
              onYes={onRemove}
              onCancel={() => setConfirming(undefined)}
            />
          ) : (
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setConfirming('remove')}
              disabled={!idle}
            >
              {t('member.remove')}
            </button>
          )}
        </div>
      )}
    </>
  );
}

type ConfirmProps = {
  question: string;
  yes: string;
  idle: boolean;
  onYes: () => void;
  onCancel: () => void;
};

/**
 * The question before a reset or a removal (the owner's choice, D25), in place of its button,
 * scrolled into view as it opens (A9; D31).
 */
function Confirm({ question, yes, idle, onYes, onCancel }: ConfirmProps) {
  const questionId = useId();
  const box = useRef<HTMLDivElement>(null);
  // Once it's drawn (its Cancel has the focus by then), the box scrolls into view, whole: the
  // page's scroll padding keeps it clear of the bars (D29), and a box already in view stays put
  // (nearest). Safari on an iPhone doesn't scroll to a focused control, so without this the
  // buttons stayed under the tabs (A7's iPhone walk). Only as it opens, not while it waits.
  useLayoutEffect(() => {
    box.current?.scrollIntoView({ block: 'nearest' });
  }, []);
  return (
    <div ref={box} className="confirm" role="group" aria-labelledby={questionId}>
      <p id={questionId}>{question}</p>
      <button type="button" className="button" onClick={onYes} disabled={!idle}>
        {yes}
      </button>
      {/* The focus goes to the way back, since the button tapped has gone. */}
      <button
        type="button"
        className="button button-quiet"
        onClick={onCancel}
        disabled={!idle}
        autoFocus
      >
        {t('app.cancel')}
      </button>
    </div>
  );
}
